import React, { useContext } from "react";
import { Route as RouterRoute, Redirect, useLocation, matchPath } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";
import { usePagePermissions } from "../context/PagePermissionsContext";
import { usePlanPermissions } from "../context/PlanPermissionsContext";
import BackdropLoading from "./BackdropLoading";

const ProtectedRoute = ({
  component: Component,
  isPrivate = false,
  isPublic = false,
  requiredPermission = null,
  planFlag = null,
  planFlags = null,
  ...rest
}) => {
  const { isAuth, loading: authLoading, user } = useContext(AuthContext);
  const { canAccessPage, loading: permissionsLoading } = usePagePermissions();
  const planPermissions = usePlanPermissions();
  const location = useLocation();

  // As rotas privadas moram todas dentro do mesmo LoggedInLayout, sem um
  // <Switch> em volta: isso faz esta funcao rodar para TODAS as paginas ao
  // mesmo tempo, em qualquer tela. Sem a guarda abaixo, a rota de /financeiro
  // decidia redirecionar mesmo com o usuario parado em /painel — dezenas de
  // <Redirect> de uma vez, estourando o limite de replaceState do Safari.
  const ehARotaAtual = matchPath(location.pathname, {
    path: rest.path,
    exact: rest.exact,
    strict: rest.strict
  });
  if (rest.path && !ehARotaAtual) {
    return <RouterRoute {...rest} component={Component} />;
  }

  // Rotas públicas (landing, login, cadastro, etc) - carrega instantaneamente sem loading
  if (isPublic) {
    // Se já está logado e tenta acessar landing/login/cadastro, redireciona para dashboard
    if (!authLoading && isAuth && (rest.path === "/" || rest.path === "/login" || rest.path === "/cadastro")) {
      return <Redirect to={{ pathname: "/painel", state: { from: rest.location } }} />;
    }
    return <RouterRoute {...rest} component={Component} />;
  }

  // Mostra loading enquanto verifica autenticação, permissões ou plano
  if (authLoading || permissionsLoading || planPermissions.loading) {
    return <BackdropLoading />;
  }

  // Bloqueia acesso se o plano não incluir o módulo necessário. So decide
  // quando o plano chegou: numa rede ruim a chamada falha, e antes disso o
  // estado padrao dizia que tudo estava desligado.
  if (isAuth && !isPublic && planPermissions.planLoaded) {
    const flags = planFlags || (planFlag ? [planFlag] : []);
    if (flags.length > 0) {
      const denied = flags.some(flag => !planPermissions[flag]);
      if (denied) {
        return <Redirect to={{ pathname: "/painel", state: { from: rest.location } }} />;
      }
    }
  }

  // Usuário não logado tentando acessar rota privada -> vai para login
  if (!isAuth && isPrivate) {
    return <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />;
  }

  // Usuário não logado em rota não marcada -> vai para login
  if (!isAuth && !isPrivate) {
    return <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />;
  }

  // Páginas liberadas para todos os usuários autenticados
  const PUBLIC_PATHS = ["/painel", "/dashboard", "/tarefas"];

  // Verifica permissões de página se não for admin
  if (isAuth && user?.profile !== "admin") {
    const pagePath = rest.path;

    // Páginas públicas não verificam permissão
    if (PUBLIC_PATHS.includes(pagePath)) {
      return <RouterRoute {...rest} component={Component} />;
    }

    // Se a página requer permissão específica e o usuário não tem acesso
    if (requiredPermission && requiredPermission !== "none" && !canAccessPage(requiredPermission)) {
      return <Redirect to={{ pathname: "/painel", state: { from: rest.location } }} />;
    }

    // Verifica acesso à página atual
    if (!canAccessPage(pagePath)) {
      return <Redirect to={{ pathname: "/painel", state: { from: rest.location } }} />;
    }
  }

  return <RouterRoute {...rest} component={Component} />;
};

export default ProtectedRoute;
